'use client';

import type { ProductDocument } from 'models/product';
import type { UserBike } from 'models/shared';
import { VehicleSelector } from '@ui/ShopByVehicle';
import { useBikeFilter } from 'context/BikeFilterContext';
import { useAuth } from 'context/AuthContext';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { AlertCircleIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { BADGE_CONFIG } from '@c/InformationBadge';
import { useRouter } from 'next/navigation';
import MobileSearchByVehicle from './SearchByVehicle/MobileSearchByVehicle';
import DesktopSearchByVehicle from './SearchByVehicle/DesktopSearchByVehicle';
import { useState } from 'react';

type ConfirmFitmentProps = {
  product: ProductDocument;
};

export default function ConfirmFitment({ product }: ConfirmFitmentProps) {
  const router = useRouter();
  const { selectedBike, setSelectedBike } = useBikeFilter();
  const { userDoc } = useAuth();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  const isBikeComplete = Boolean(
    selectedBike.type &&
      selectedBike.make &&
      selectedBike.year &&
      selectedBike.model
  );

  const fitment = selectedBike.type
    ? getBikeFitment(
        product.fitting_bikes?.bikes ?? [],
        [selectedBike as UserBike],
        !!product.integration_info
      )
    : null;

  const handleBikeComplete = (bike: UserBike) => {
    if (bike.type && bike.make && bike.model && bike.year) {
      setSelectedBike(bike);
    }
  };

  const handleFindItems = () => {
    if (
      !selectedBike.type ||
      !selectedBike.make ||
      !selectedBike.model ||
      !selectedBike.year
    )
      return;

    const year = selectedBike.year;
    const make = selectedBike.make;
    const model = selectedBike.model;
    const bikeParam = `?bikes=${year}:${make}:${model}`;

    router.push(`/shop/all${bikeParam}`);
  };

  const FitmentMessage = () => {
    if (fitment?.status === 'fits') {
      return (
        <div className="flex flex-col items-start gap-y-2 sm:flex-row sm:gap-x-2 sm:gap-y-0">
          <div className="flex items-start gap-x-2">
            <CheckCircleIcon
              className="h-[2.5rem] w-[2.5rem]"
              style={{ color: BADGE_CONFIG.success.foreground }}
            />
            <span className="w-[35rem] truncate pt-1 text-[1.6rem] font-medium">
              Fits your {selectedBike.make} {selectedBike.year}{' '}
              {selectedBike.model}.
            </span>
          </div>
        </div>
      );
    }

    if (fitment?.status === 'may-fit') {
      return (
        <div className="flex flex-col items-start gap-y-2 sm:flex-row sm:gap-x-2 sm:gap-y-0">
          <div className="flex items-start gap-x-2">
            <AlertCircleIcon
              className="h-[2.5rem] w-[2.5rem]"
              style={{ color: BADGE_CONFIG.warning.foreground }}
            />
            <span className="w-[35rem] truncate pt-1 text-[1.6rem] font-medium">
              May fit your {selectedBike.make} {selectedBike.year}{' '}
              {selectedBike.model}.
            </span>
          </div>
        </div>
      );
    }

    if (fitment?.status === 'does-not-fit') {
      return (
        <div className="flex flex-col items-start gap-y-2 sm:flex-row sm:gap-x-2 sm:gap-y-0">
          <div className="flex items-start gap-x-2">
            <XCircleIcon
              className="h-[2.5rem] w-[2.5rem]"
              style={{ color: BADGE_CONFIG.error.foreground }}
            />
            <span className="w-[35rem] truncate pt-1 text-[1.6rem] font-medium">
              Does not fit your vehicle.
            </span>
          </div>

          <button
            type="button"
            onClick={handleFindItems}
            className="pl-0 pt-12 text-[1.6rem] font-medium text-brand-secondary underline sm:pl-8 sm:pt-1"
          >
            Find an item that fits your bike
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="-mt-8 sm:mt-0 sm:p-4">
      <div className="flex w-full flex-col items-center justify-between gap-y-8 rounded-[2rem] border border-zinc-200 p-[2rem] sm:flex-row">
        <div className="flex w-full flex-col gap-y-2">
          <h3 className="text-[2.1rem] font-semibold text-black">
            Confirm this item fits
          </h3>

          <h3 className="text-[1.8rem] text-zinc-500">
            Add your vehicle details to make sure this item fits.
          </h3>
        </div>

        <div className="flex w-full flex-col gap-y-8">
          <VehicleSelector
            onBikeComplete={handleBikeComplete}
            setShowMobileSearchByVehicle={setShowMobileSearchByVehicle}
            setShowDesktopSearchByVehicle={setShowDesktopSearchByVehicle}
          />

          <div className="flex w-full sm:justify-end">
            {isBikeComplete && <FitmentMessage />}
          </div>

          <MobileSearchByVehicle
            show={showMobileSearchByVehicle}
            dismiss={() => setShowMobileSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
            userDoc={userDoc}
          />

          <DesktopSearchByVehicle
            isOpen={showDesktopSearchByVehicle}
            dismiss={() => setShowDesktopSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
          />
        </div>
      </div>
    </div>
  );
}
